import GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import moment from 'moment'
import { CarHireSearchSortTypes } from './CarHireSearchPage/CarHireSearchPageSort'
import { fillArray, partitionBy, sortBy } from 'lib/arrayUtils'
import { CarHireSearchRecommendedPriority } from 'constants/carHire'
import { getAge } from 'lib/dateUtils'

export function getCarHireListKey(filters: App.CarHireOfferListFilters) {
  const tokens = [
    filters.driversAgeCategory,
    filters.dropOffDate,
    filters.dropOffTime,
    filters.pickUpDate,
    filters.pickUpTime,
    filters.pickUpAirportCode,
    filters.dropOffAirportCode,
    filters.driversAge,
  ]

  return tokens.join('-')
}

function mapParamsToSearchItem(id?: string, name?: string): App.SearchItem {
  if (id && name) {
    if (id.length === 3) {
      return {
        searchType: 'airport',
        airportCode: id,
        format: {
          mainText: name,
        },
      } as App.SearchAirport
    }

    return {
      searchType: 'destination',
      value: id,
      format: {
        mainText: name,
      },
    }
  }
}

export function getCarHireSearchStateFromParams(queryParams: URLSearchParams): Partial<GlobalSearchState> {
  const ageCategory = queryParams.get('ageCategory')
  const pickUpItem = mapParamsToSearchItem(queryParams.get('pickUpId'), queryParams.get('pickUpName'))
  const dropOffItem = mapParamsToSearchItem(queryParams.get('dropOffId'), queryParams.get('dropOffName'))
  const pickUpDate = queryParams.get('pickUpDate')
  const dropoffDate = queryParams.get('dropOffDate')
  const age = queryParams.get('age')

  return {
    searchItem: pickUpItem,
    location: pickUpItem?.format.mainText,
    secondarySearchItem: dropOffItem,
    secondaryLocation: dropOffItem?.format.mainText,
    checkinDate: pickUpDate ? moment(pickUpDate) : undefined,
    checkoutDate: dropoffDate ? moment(dropoffDate) : undefined,
    pickUpTime: queryParams.get('pickUpTime'),
    dropOffTime: queryParams.get('dropOffTime'),
    driversAgeCategory: ageCategory ? parseInt(ageCategory) as App.CarHireDriverAgeCategory : undefined,
    driversAge: age ? parseInt(age) : undefined,
  }
}

export function sortCarHireOffers(offers: Array<App.CarHireOffer>, sort: CarHireSearchSortTypes) {
  switch (sort) {
    case 'price.asc':
    case 'price.desc':
      return sortBy(offers, res => res.price, sort === 'price.asc' ? 'asc' : 'desc')
    case 'rating.asc':
    case 'rating.desc':
      return sortBy(offers, res => res.rating ?? 0, sort === 'rating.asc' ? 'asc' : 'desc')
    default:
      // Recommended is the priority brands at the top and all offer shuffled in a way that is stable but still...shuffled
      const sorted = sortBy(fillArray(offers.length), index => (index << 5 - index) * index % 5).map(index => offers[index])
      const [priorityOffers, others] = partitionBy(sorted, offer => CarHireSearchRecommendedPriority.has(offer.vendor.name))

      return [
        ...priorityOffers,
        ...others,
      ]
  }
}

/**
 * Works out and returns the drivers age category based on the age provider
 * @param age The age to get the category for
 * @returns The category for the given age
 */
export function getCarHireAgeCategory(age: number): App.CarHireDriverAgeCategory {
  if (age <= 29) {
    return 1
  } else if (age >= 70) {
    return 3
  }
  return 2
}

export function isCarHireItemDobValid(dob: string, item: App.Checkout.CarHireItem): boolean {
  const age = getAge(dob)
  const ageCategory = getCarHireAgeCategory(age)
  return ageCategory === 2 || age === item.age
}
